import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  User as ProfileIcon,
  ShoppingBag as OrdersIcon,
  MapPin as AddressIcon,
  LogOut as LogoutIcon,
  Plus,
  Trash2,
  Lock,
  Loader,
  Eye,
  CheckCircle,
  Clock,
  ChevronRight,
  ShieldCheck
} from "lucide-react";
import { API_BASE_URL } from "../config";

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  
  // Profile Form State
  const [profileForm, setProfileForm] = useState({
    login_name: "",
    login_phone: "",
    login_address: "",
    login_email: "",
    login_password: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  // New Address State
  const [newAddress, setNewAddress] = useState("");

  // Expanded Order ID (for listing items in order)
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  const navigate = useNavigate();

  // Load user from localStorage
  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    if (savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      setProfileForm({
        login_name: parsedUser.login_name || parsedUser.name || "",
        login_phone: parsedUser.login_phone || parsedUser.phone || "",
        login_address: parsedUser.login_address || parsedUser.address || "",
        login_email: parsedUser.login_email || parsedUser.email || "",
        login_password: "",
      });
    }
  }, []);

  // Fetch orders when orders tab or overview is active
  useEffect(() => {
    if (!user) return;
    if (activeTab === "orders" || activeTab === "overview") {
      fetchOrders();
    }
  }, [user, activeTab]);

  // Fetch addresses when address tab is active
  useEffect(() => {
    if (!user) return;
    if (activeTab === "address") {
      fetchAddresses();
    }
  }, [user, activeTab]);

  const fetchOrders = async () => {
    setOrdersLoading(true);
    try {
      const params = {
        login_name: user.login_name || user.name,
        login_email: user.login_email || user.email,
        login_phone: user.login_phone || user.phone,
      };
      const response = await axios.get(`${API_BASE_URL}/login-order`, { params });
      setOrders(response.data || []);
    } catch (err) {
      console.error("Error fetching orders:", err);
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchAddresses = async () => {
    setAddressLoading(true);
    try {
      const loginId = user.login_id || user.LOGIN_ID;
      const response = await axios.get(`${API_BASE_URL}/get-shipping-addresses`, {
        params: { LOGIN_ID: loginId }
      });
      setAddresses(response.data.SHIPPING_ADDRESS || []);
    } catch (err) {
      console.error("Error fetching addresses:", err);
    } finally {
      setAddressLoading(false);
    }
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    if (!newAddress.trim()) return;

    setAddressLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const loginId = user.login_id || user.LOGIN_ID;
      await axios.post(`${API_BASE_URL}/save-shipping-address`, {
        LOGIN_ID: loginId,
        shipping_address: newAddress.trim()
      });
      setNewAddress("");
      setSuccessMessage("Address added successfully!");
      fetchAddresses();
    } catch (err) {
      setErrorMessage("Failed to add shipping address.");
      console.error(err);
    } finally {
      setAddressLoading(false);
    }
  };

  const handleDeleteAddress = async (addressText) => {
    setAddressLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const loginId = user.login_id || user.LOGIN_ID;
      await axios.post(`${API_BASE_URL}/delete-shipping-address`, {
        LOGIN_ID: loginId,
        shipping_address: addressText
      });
      setSuccessMessage("Address deleted successfully!");
      fetchAddresses();
    } catch (err) {
      setErrorMessage("Failed to delete shipping address.");
      console.error(err);
    } finally {
      setAddressLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    const loginId = user.login_id || user.LOGIN_ID;
    if (!loginId) {
      setErrorMessage("Login ID is missing. Please log in again.");
      setProfileLoading(false);
      return;
    }

    try {
      const payload = { ...profileForm };
      if (!payload.login_password) {
        delete payload.login_password; // Don't send empty password
      }

      const response = await axios.put(`${API_BASE_URL}/edit-user/${loginId}`, payload);
      if (response.status === 200) {
        // Update user state and localStorage
        const updatedUser = {
          ...user,
          login_name: profileForm.login_name,
          name: profileForm.login_name,
          login_phone: profileForm.login_phone,
          phone: profileForm.login_phone,
          login_address: profileForm.login_address,
          address: profileForm.login_address,
          login_email: profileForm.login_email,
          email: profileForm.login_email,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setUser(updatedUser);
        setSuccessMessage("Profile updated successfully!");
        setProfileForm((prev) => ({ ...prev, login_password: "" }));
      }
    } catch (err) {
      setErrorMessage("Failed to update profile. Email might be in use.");
      console.error(err);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    setUser(null);
    window.location.href = "/";
  };

  // If no user is logged in
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full text-center p-8 bg-white rounded-3xl shadow-lg border">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 text-red-800 mb-4">
            <Lock size={24} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-500 mb-6">
            Please log in or register to access your personal dashboard and track orders.
          </p>
          <button
            onClick={() => navigate("/loginRegistor")}
            className="w-full bg-red-900 hover:bg-red-800 text-white font-bold py-3 rounded-xl transition shadow-md"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Calculate some simple overview stats
  const totalOrders = orders.length;
  const totalSpent = orders.reduce((sum, o) => sum + (parseFloat(o.total) || 0), 0);
  const totalSaved = orders.reduce((sum, o) => {
    const items = Array.isArray(o.cart_data) ? o.cart_data : [];
    const savedOnOrder = items.reduce((sSum, item) => {
      const price = parseFloat(item.rate) || 0;
      const qty = parseInt(item.quantity) || 0;
      const disc = parseFloat(item.discount) || 0;
      return sSum + (price * qty * (disc / 100));
    }, 0);
    return sum + savedOnOrder;
  }, 0);

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row gap-8">
          
          {/* Sidebar Menu */}
          <aside className="w-full md:w-64 bg-white rounded-3xl p-6 shadow-sm border border-gray-100 shrink-0 h-fit">
            <div className="flex items-center gap-3 mb-6 pb-6 border-b">
              <div className="w-12 h-12 bg-red-800 text-white font-black text-xl rounded-full flex items-center justify-center font-serif">
                {user.name?.charAt(0).toUpperCase() || "C"}
              </div>
              <div className="min-w-0">
                <p className="font-bold text-gray-900 truncate capitalize">{user.name}</p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
            </div>

            <nav className="space-y-1.5">
              {[
                { key: "overview", label: "Dashboard Overview", icon: ShieldCheck },
                { key: "orders", label: "My Orders", icon: OrdersIcon },
                { key: "profile", label: "My Profile", icon: ProfileIcon },
                { key: "address", label: "Manage Addresses", icon: AddressIcon }
              ].map((item) => (
                <button
                  key={item.key}
                  onClick={() => {
                    setActiveTab(item.key);
                    setSuccessMessage("");
                    setErrorMessage("");
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl text-sm font-semibold transition ${
                    activeTab === item.key
                      ? "bg-red-800 text-white shadow-sm"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <item.icon size={18} />
                    <span>{item.label}</span>
                  </div>
                  <ChevronRight size={14} className={activeTab === item.key ? "text-white" : "text-gray-400"} />
                </button>
              ))}

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-semibold text-red-600 hover:bg-red-50 transition mt-6"
              >
                <LogoutIcon size={18} />
                <span>Logout Session</span>
              </button>
            </nav>
          </aside>

          {/* Main Dashboard Content */}
          <main className="flex-1 bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-gray-100">
            {successMessage && (
              <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-xl text-sm text-green-700 font-medium mb-6">
                ✅ {successMessage}
              </div>
            )}

            {errorMessage && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-xl text-sm text-red-700 font-medium mb-6">
                ⚠️ {errorMessage}
              </div>
            )}

            {/* TAB: OVERVIEW */}
            {activeTab === "overview" && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Welcome Back, {user.name}!</h2>
                  <p className="text-gray-500 text-sm">Monitor your orders, shipping details, and customize your profile.</p>
                </div>

                {/* Stat Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div className="bg-red-50/50 border border-red-100 rounded-3xl p-6 flex flex-col justify-between">
                    <span className="text-xs font-bold text-red-800 uppercase tracking-wider">Total Orders</span>
                    <span className="text-3xl font-black text-red-950 mt-4">{totalOrders}</span>
                  </div>
                  <div className="bg-green-50/50 border border-green-100 rounded-3xl p-6 flex flex-col justify-between">
                    <span className="text-xs font-bold text-green-800 uppercase tracking-wider">Total Spent</span>
                    <span className="text-3xl font-black text-green-950 mt-4">₹{Math.round(totalSpent)}</span>
                  </div>
                  <div className="bg-yellow-50/50 border border-yellow-100 rounded-3xl p-6 flex flex-col justify-between">
                    <span className="text-xs font-bold text-yellow-800 uppercase tracking-wider">Wholesale Savings</span>
                    <span className="text-3xl font-black text-yellow-950 mt-4">₹{Math.round(totalSaved)}</span>
                  </div>
                </div>

                {/* Recent Orders Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-gray-900">Recent Purchases</h3>
                  {ordersLoading ? (
                    <div className="flex justify-center py-6"><Loader className="animate-spin text-red-800" /></div>
                  ) : orders.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No orders found. Add items to your cart and place your first order!</p>
                  ) : (
                    <div className="border border-gray-100 rounded-2xl overflow-hidden divide-y">
                      {orders.slice(0, 3).map((order) => (
                        <div key={order.order_id} className="p-4 flex items-center justify-between text-sm hover:bg-gray-50/50">
                          <div>
                            <p className="font-bold text-gray-950">Order #{order.order_id}</p>
                            <p className="text-xs text-gray-500">Total: ₹{order.total} | {order.payment_method}</p>
                          </div>
                          <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${
                            order.status?.toLowerCase() === "delivered" 
                              ? "bg-green-100 text-green-800" 
                              : order.status?.toLowerCase() === "pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-blue-100 text-blue-800"
                          }`}>
                            {order.status || "Pending"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* TAB: MY ORDERS */}
            {activeTab === "orders" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Purchase History</h2>
                  <p className="text-gray-500 text-sm">Detailed overview of all previous estimates and purchases.</p>
                </div>

                {ordersLoading ? (
                  <div className="flex justify-center py-12"><Loader className="animate-spin text-red-800" size={32} /></div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-12 border border-dashed rounded-3xl">
                    <p className="text-gray-500 font-medium">You have not placed any orders yet.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => {
                      const items = Array.isArray(order.cart_data) ? order.cart_data : [];
                      const isExpanded = expandedOrderId === order.order_id;
                      
                      return (
                        <div key={order.order_id} className="border border-gray-200 rounded-3xl overflow-hidden shadow-sm bg-white">
                          {/* Order Header Card */}
                          <div className="p-5 bg-gray-50/50 flex flex-wrap gap-4 items-center justify-between border-b">
                            <div className="space-y-0.5">
                              <p className="text-xs text-gray-500 font-bold uppercase">Order Reference</p>
                              <p className="text-base font-black text-gray-950">#{order.order_id}</p>
                            </div>
                            <div className="space-y-0.5">
                              <p className="text-xs text-gray-500 font-bold uppercase">Overall Amount</p>
                              <p className="text-base font-bold text-green-700">₹{order.total}</p>
                            </div>
                            <div className="space-y-0.5">
                              <p className="text-xs text-gray-500 font-bold uppercase">Payment</p>
                              <p className="text-sm font-semibold text-gray-700 capitalize">{order.payment_method || "COD"}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`px-3 py-1 rounded-full text-xs font-black uppercase ${
                                order.status?.toLowerCase() === "delivered" 
                                  ? "bg-green-100 text-green-800" 
                                  : order.status?.toLowerCase() === "pending"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}>
                                {order.status || "Pending"}
                              </span>
                              <button
                                onClick={() => setExpandedOrderId(isExpanded ? null : order.order_id)}
                                className="p-2 border rounded-xl hover:bg-gray-100 transition flex items-center gap-1.5 text-xs font-bold"
                              >
                                <Eye size={16} />
                                <span>{isExpanded ? "Collapse" : "Items"}</span>
                              </button>
                            </div>
                          </div>

                          {/* Expanded Order Items */}
                          {isExpanded && (
                            <div className="p-5 border-t divide-y divide-gray-100">
                              <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm border-collapse">
                                  <thead>
                                    <tr className="text-gray-500 font-bold text-xs border-b">
                                      <th className="pb-3">Product Item</th>
                                      <th className="pb-3 text-center">Qty Ordered</th>
                                      <th className="pb-3 text-right">Unit Price</th>
                                      <th className="pb-3 text-right">Flat Disc</th>
                                      <th className="pb-3 text-right">Final Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {items.map((item, idx) => {
                                      const price = parseFloat(item.rate) || 0;
                                      const qty = parseInt(item.quantity) || 0;
                                      const disc = parseFloat(item.discount) || 0;
                                      const discountedRate = Math.round(price * (1 - disc / 100));
                                      const finalPrice = discountedRate * qty;

                                      return (
                                        <tr key={idx} className="border-b last:border-b-0">
                                          <td className="py-3 font-semibold text-gray-900">
                                            {item.name || item.subName}
                                          </td>
                                          <td className="py-3 text-center font-bold text-gray-700">{qty}</td>
                                          <td className="py-3 text-right text-gray-500">₹{price}</td>
                                          <td className="py-3 text-right text-red-600 font-semibold">{disc}% OFF</td>
                                          <td className="py-3 text-right font-bold text-green-700">₹{finalPrice}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                              
                              {order.transaction_id && (
                                <div className="pt-4 text-xs text-gray-500 flex justify-between">
                                  <span>Transaction ID: <strong>{order.transaction_id}</strong></span>
                                  <span>Payment Status: <strong className="text-green-700 capitalize">{order.payment_status || "Completed"}</strong></span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* TAB: MY PROFILE */}
            {activeTab === "profile" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Personal Profile Settings</h2>
                  <p className="text-gray-500 text-sm">Change your profile credentials or update your login password.</p>
                </div>

                <form onSubmit={handleProfileUpdate} className="space-y-4 max-w-xl">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider">Full Name</label>
                    <input
                      type="text"
                      value={profileForm.login_name}
                      onChange={(e) => setProfileForm({ ...profileForm, login_name: e.target.value })}
                      required
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-800 text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider">Mobile Phone</label>
                      <input
                        type="tel"
                        value={profileForm.login_phone}
                        onChange={(e) => setProfileForm({ ...profileForm, login_phone: e.target.value })}
                        required
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-800 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider">Email Address</label>
                      <input
                        type="email"
                        value={profileForm.login_email}
                        onChange={(e) => setProfileForm({ ...profileForm, login_email: e.target.value })}
                        required
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-800 text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider">Registered Address</label>
                    <textarea
                      value={profileForm.login_address}
                      onChange={(e) => setProfileForm({ ...profileForm, login_address: e.target.value })}
                      required
                      rows="3"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-800 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider">New Password (leave empty to keep current)</label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={profileForm.login_password}
                        onChange={(e) => setProfileForm({ ...profileForm, login_password: e.target.value })}
                        placeholder="••••••••"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-800 text-sm pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-4 flex items-center text-xs font-bold text-gray-500 hover:text-red-800"
                      >
                        {showPassword ? "HIDE" : "SHOW"}
                      </button>
                    </div>
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={profileLoading}
                      className="bg-red-800 hover:bg-red-700 text-white font-bold py-2.5 px-6 rounded-xl transition disabled:bg-gray-400 flex items-center gap-2 text-sm shadow-sm"
                    >
                      {profileLoading && <Loader className="animate-spin" size={16} />}
                      <span>Save Changes</span>
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* TAB: MANAGE ADDRESSES */}
            {activeTab === "address" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Manage Shipping Locations</h2>
                  <p className="text-gray-500 text-sm">Add or delete delivery locations for quicker checkout operations.</p>
                </div>

                {/* Add Address Form */}
                <form onSubmit={handleAddAddress} className="flex gap-3 max-w-xl">
                  <input
                    type="text"
                    value={newAddress}
                    onChange={(e) => setNewAddress(e.target.value)}
                    required
                    placeholder="Enter new delivery address..."
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-800 text-sm"
                  />
                  <button
                    type="submit"
                    disabled={addressLoading}
                    className="bg-red-850 hover:bg-red-800 text-white font-bold px-5 rounded-xl transition flex items-center gap-1.5 text-xs sm:text-sm shadow-sm shrink-0"
                  >
                    <Plus size={16} />
                    <span>Add Location</span>
                  </button>
                </form>

                {addressLoading ? (
                  <div className="flex justify-center py-6"><Loader className="animate-spin text-red-800" /></div>
                ) : addresses.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">No alternative addresses configured. Using your registered profile address by default.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {addresses.map((addr) => (
                      <div key={addr.id} className="border border-gray-150 rounded-2xl p-4 flex justify-between items-start gap-4 hover:shadow-xs transition bg-white shadow-xs">
                        <div className="text-xs sm:text-sm text-gray-700 leading-relaxed font-medium">
                          {addr.details}
                        </div>
                        <button
                          onClick={() => handleDeleteAddress(addr.details)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1.5 rounded-lg transition shrink-0"
                          title="Delete address"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;